import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from './Tooltip';
import './EntityHeader.css';

type ActionOption = {
  id: string;
  label: string;
  onClick?: () => void;
};

type CollapsibleAction = {
  id: string;
  label: string;
  icon: string;
  onClick?: () => void;
  options?: ActionOption[];
  toggleLabels?: readonly [string, string];
  alwaysInOverflow?: boolean;
};

type HeaderProps = {
  title: string;
  titleMeta?: React.ReactNode;
  /** Tooltip content shown on hover over the H1 (interactive). */
  titleTooltip?: React.ReactNode;
  showBack?: boolean;
  onBack?: () => void;
  showStatus?: boolean;
  statusTone?: 'success' | 'warning' | 'danger' | 'neutral' | 'info';
  statusLabel?: string;
  showInfo?: boolean;
  infoText?: string;
  showInfoExtras?: boolean;
  infoExtras?: React.ReactNode;
  collapsibleActions: CollapsibleAction[];
  // Controlado desde el padre para poder renderizar la tabla fuera del header
  isFilterOpen?: boolean;
  onFilterOpenChange?: (open: boolean) => void;
  filterPanelRef?: React.RefObject<HTMLElement | null>;
  // Callback opcional al abrir
  onFilter?: () => void;
  onPublish?: () => void;
  filterLabel?: string;
  publishLabel?: string;
  showFilterAction?: boolean;
  showActions?: boolean;
};

export function EntityHeader({
  title,
  titleMeta,
  titleTooltip,
  showBack = false,
  onBack,
  showStatus = false,
  statusTone = 'success',
  statusLabel = 'Status',
  showInfo = false,
  infoText,
  showInfoExtras = false,
  infoExtras,
  collapsibleActions,
  isFilterOpen: isFilterOpenProp = false,
  onFilterOpenChange,
  filterPanelRef,
  onFilter,
  onPublish,
  filterLabel = 'Filter (1)',
  publishLabel = 'Publish',
  showFilterAction = true,
  showActions = true,
}: HeaderProps) {
  const actionsAreaRef = useRef<HTMLDivElement>(null);
  const fixedActionsRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const mainRowRef = useRef<HTMLDivElement>(null);
  const titleGroupRef = useRef<HTMLDivElement>(null);
  const overflowMeasureRef = useRef<HTMLButtonElement>(null);
  const actionMeasureRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const rafRef = useRef<number | null>(null);
  const areActionsStackedRef = useRef(false);
  const [visibleCount, setVisibleCount] = useState(collapsibleActions.length);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [areActionsStacked, setAreActionsStacked] = useState(false);
  const [openInlineDropdownId, setOpenInlineDropdownId] = useState<string | null>(null);
  const [openOverflowDropdownId, setOpenOverflowDropdownId] = useState<string | null>(null);
  const [selectedLabelsByAction, setSelectedLabelsByAction] = useState<Record<string, string>>({});
  const isFilterOpen = isFilterOpenProp;

  const displayedActions = useMemo(
    () =>
      collapsibleActions.map((action) => ({
        ...action,
        displayLabel: selectedLabelsByAction[action.id] ?? action.label,
      })),
    [collapsibleActions, selectedLabelsByAction]
  );
  const inlineCandidates = useMemo(
    () => displayedActions.filter((action) => !action.alwaysInOverflow),
    [displayedActions]
  );

  useLayoutEffect(() => {
    const MAIN_ROW_GAP = 24;
    const UNSTACK_HYSTERESIS = 32;

    const countVisibleActions = (
      actionsAreaWidth: number,
      fixedWidth: number,
      overflowWidth: number,
      widths: number[]
    ) => {
      const available = Math.max(0, actionsAreaWidth - fixedWidth);
      const actionGap = 4;

      let used = 0;
      let count = 0;
      for (let i = 0; i < widths.length; i += 1) {
        const next = widths[i] + (count > 0 ? actionGap : 0);
        if (used + next <= available) {
          used += next;
          count += 1;
        } else {
          break;
        }
      }

      const hiddenCount = widths.length - count;
      if (hiddenCount > 0) {
        let usedWithOverflow = used + (count > 0 ? actionGap : 0) + overflowWidth;
        while (count > 0 && usedWithOverflow > available) {
          const removedWidth = widths[count - 1];
          used -= removedWidth;
          if (count > 1) {
            used -= actionGap;
          }
          count -= 1;
          usedWithOverflow = used + (count > 0 ? actionGap : 0) + overflowWidth;
        }
      }

      return Math.max(0, Math.min(count, widths.length));
    };

    const calculateLayout = () => {
      const container = actionsAreaRef.current;
      const fixedActions = fixedActionsRef.current;
      const overflowMeasure = overflowMeasureRef.current;
      const mainRow = mainRowRef.current;
      const titleGroup = titleGroupRef.current;
      if (!container || !fixedActions || !overflowMeasure) return;

      const fixedWidth = fixedActions.offsetWidth;
      const overflowWidth = overflowMeasure.offsetWidth;
      const widths = inlineCandidates
        .map((_, index) => actionMeasureRefs.current[index]?.offsetWidth ?? 0)
        .filter((value) => value > 0);

      const isStacked = areActionsStackedRef.current;

      const countForActionsWidth = (actionsAreaWidth: number) =>
        countVisibleActions(actionsAreaWidth, fixedWidth, overflowWidth, widths);

      if (!showActions || !mainRow || !titleGroup) {
        if (areActionsStackedRef.current) {
          areActionsStackedRef.current = false;
          setAreActionsStacked(false);
        }
        setVisibleCount((prev) => {
          const next = countForActionsWidth(container.offsetWidth);
          return prev === next ? prev : next;
        });
        return;
      }

      const actionsGap = 10;
      const collapsibleMargin = 6;

      const getMinActionsWidth = (visibleInlineCount: number) => {
        const hasOverflowMenu =
          inlineCandidates.length === 0
            ? displayedActions.length > 0
            : visibleInlineCount < inlineCandidates.length;

        return (
          fixedWidth +
          actionsGap +
          (hasOverflowMenu ? collapsibleMargin + overflowWidth : 0)
        );
      };

      const measureTitleIntrinsicWidth = () => {
        const titleGap = 8;
        const children = Array.from(titleGroup.children).filter(
          (node): node is HTMLElement => node instanceof HTMLElement
        );
        if (children.length === 0) {
          return titleGroup.scrollWidth;
        }

        return children.reduce(
          (total, child, index) => total + child.scrollWidth + (index > 0 ? titleGap : 0),
          0
        );
      };

      const titleRequiredWidth = measureTitleIntrinsicWidth();

      const availableActionsWidthOnSingleRow = () =>
        Math.max(0, mainRow.clientWidth - titleRequiredWidth - MAIN_ROW_GAP);

      const fitsOnSingleRow = (visibleInlineCount: number, rowWidth = mainRow.clientWidth) =>
        titleRequiredWidth + MAIN_ROW_GAP + getMinActionsWidth(visibleInlineCount) <=
        rowWidth + 1;

      const inlineOnCurrentRow = countForActionsWidth(container.offsetWidth);
      const visibleIfSingleRow = countForActionsWidth(availableActionsWidthOnSingleRow());

      let nextStacked = isStacked;

      if (!isStacked) {
        if (inlineOnCurrentRow > 0) {
          nextStacked = false;
        } else {
          const titleRect = titleGroup.getBoundingClientRect();
          const actionsRect = container.getBoundingClientRect();
          const visualOverlap = titleRect.right > actionsRect.left - 0.5;
          nextStacked = !fitsOnSingleRow(0) || visualOverlap;
        }
      } else {
        const canUnstack =
          visibleIfSingleRow > 0 ||
          fitsOnSingleRow(0, mainRow.clientWidth - UNSTACK_HYSTERESIS);
        nextStacked = !canUnstack;
      }

      const willUnstack = isStacked && !nextStacked;

      if (nextStacked !== isStacked) {
        areActionsStackedRef.current = nextStacked;
        setAreActionsStacked(nextStacked);
      }

      let nextVisibleCount: number;
      if (nextStacked) {
        const stackedActionsWidth =
          isStacked || container.offsetWidth >= mainRow.clientWidth - 1
            ? container.offsetWidth
            : mainRow.clientWidth;
        nextVisibleCount = countForActionsWidth(stackedActionsWidth);
      } else if (willUnstack) {
        nextVisibleCount = visibleIfSingleRow;
      } else {
        nextVisibleCount = inlineOnCurrentRow;
      }

      setVisibleCount((prev) => (prev === nextVisibleCount ? prev : nextVisibleCount));
    };

    const scheduleRecalc = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(calculateLayout);
    };

    scheduleRecalc();

    const resizeObserver = new ResizeObserver(() => scheduleRecalc());
    if (headerRef.current) resizeObserver.observe(headerRef.current);
    if (mainRowRef.current) resizeObserver.observe(mainRowRef.current);
    if (titleGroupRef.current) resizeObserver.observe(titleGroupRef.current);
    if (actionsAreaRef.current) resizeObserver.observe(actionsAreaRef.current);
    if (fixedActionsRef.current) resizeObserver.observe(fixedActionsRef.current);

    window.addEventListener('resize', scheduleRecalc);
    window.addEventListener('orientationchange', scheduleRecalc);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => scheduleRecalc());
    }

    return () => {
      window.removeEventListener('resize', scheduleRecalc);
      window.removeEventListener('orientationchange', scheduleRecalc);
      resizeObserver.disconnect();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [
    inlineCandidates,
    displayedActions.length,
    showActions,
    showBack,
    showStatus,
    showInfo,
    showInfoExtras,
    title,
    titleMeta,
    infoText,
    infoExtras,
    showFilterAction,
    filterLabel,
    publishLabel,
  ]);

  const visibleActions = useMemo(
    () => inlineCandidates.slice(0, visibleCount),
    [inlineCandidates, visibleCount]
  );
  const hiddenInlineActions = useMemo(
    () => inlineCandidates.slice(visibleCount),
    [inlineCandidates, visibleCount]
  );
  const hiddenInlineIds = useMemo(
    () => new Set(hiddenInlineActions.map((action) => action.id)),
    [hiddenInlineActions]
  );
  const collapsedActions = useMemo(
    () =>
      displayedActions.filter(
        (action) => action.alwaysInOverflow || hiddenInlineIds.has(action.id)
      ),
    [displayedActions, hiddenInlineIds]
  );

  useEffect(() => {
    if (collapsedActions.length === 0) {
      setIsMenuOpen(false);
    }
  }, [collapsedActions.length]);

  useEffect(() => {
    const onGlobalPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideHeader = headerRef.current?.contains(target);
      const clickedInsidePanel = filterPanelRef?.current?.contains(target);

      if (!clickedInsideHeader && !clickedInsidePanel) {
        setIsMenuOpen(false);
        setOpenInlineDropdownId(null);
        setOpenOverflowDropdownId(null);
        onFilterOpenChange?.(false);
      }
    };

    document.addEventListener('mousedown', onGlobalPointerDown);
    return () => document.removeEventListener('mousedown', onGlobalPointerDown);
  }, []);

  const handlePrimaryAction = (action: CollapsibleAction, location: 'inline' | 'overflow') => {
    if (action.options && action.options.length > 0) {
      if (location === 'inline') {
        setOpenInlineDropdownId((prev) => (prev === action.id ? null : action.id));
        setOpenOverflowDropdownId(null);
      } else {
        setOpenOverflowDropdownId((prev) => (prev === action.id ? null : action.id));
        setOpenInlineDropdownId(null);
      }
      return;
    }

    if (action.toggleLabels) {
      setSelectedLabelsByAction((previous) => {
        const current = previous[action.id] ?? action.label;
        const toggleLabels = action.toggleLabels;
        if (!toggleLabels) {
          return previous;
        }
        const [firstLabel, secondLabel] = toggleLabels;
        const next = current === firstLabel ? secondLabel : firstLabel;
        return { ...previous, [action.id]: next };
      });
    }

    action.onClick?.();
    setIsMenuOpen(false);
    setOpenInlineDropdownId(null);
    setOpenOverflowDropdownId(null);
  };

  return (
    <header
      className={`entity-header ${isFilterOpen ? 'entity-header-filter-open' : ''} ${
        areActionsStacked ? 'entity-header-actions-stacked' : ''
      }`}
      ref={headerRef}
    >
      <div className="eh-main-row" ref={mainRowRef}>
        <div className="eh-title-group" ref={titleGroupRef}>
          {showBack && (
            <button
              type="button"
              className="eh-back-button"
              aria-label="Go back"
              onClick={onBack}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                arrow_back
              </span>
            </button>
          )}

          {showStatus && (
            <span
              className={`eh-status-dot eh-status-dot-${statusTone}`}
              title={statusLabel}
              aria-label={statusLabel}
              role="img"
            />
          )}

          <div className="eh-title-text">
            <div className="eh-title-text-core">
              {titleTooltip ? (
                <Tooltip content={titleTooltip} trigger="hover" placement="bottom" asBlock>
                  <h1>{title}</h1>
                </Tooltip>
              ) : (
                <h1>{title}</h1>
              )}

              {(showInfo || titleMeta) && (
                <div className="eh-title-info">
                  {showInfo && infoText ? <span className="eh-title-meta">{infoText}</span> : null}
                  {!showInfo && titleMeta ? <div className="eh-title-meta">{titleMeta}</div> : null}
                </div>
              )}
            </div>

            {showInfoExtras && infoExtras ? (
              <div className="eh-title-info-extras">{infoExtras}</div>
            ) : null}
          </div>
        </div>

        {showActions && (
        <div className="eh-actions" ref={actionsAreaRef}>
          <div className="eh-collapsible-actions">
            {visibleActions.map((action) => {
              const hasOptions = Boolean(action.options?.length);
              const isOpen = openInlineDropdownId === action.id;

              return (
                <div key={action.id} className="eh-action-with-menu">
                  <button
                    type="button"
                    className="eh-collapsible-button"
                    onClick={() => handlePrimaryAction(action, 'inline')}
                    title={action.label}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      {action.icon}
                    </span>
                    <span>{action.displayLabel}</span>
                  </button>

                  {hasOptions && isOpen && (
                    <div className="eh-inline-dropdown eh-animate-in" role="menu">
                      {action.options?.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className="eh-inline-dropdown-item"
                          role="menuitem"
                          onClick={() => {
                            setSelectedLabelsByAction((previous) => ({
                              ...previous,
                              [action.id]: option.label,
                            }));
                            option.onClick?.();
                            setOpenInlineDropdownId(null);
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {collapsedActions.length > 0 && (
              <div className="eh-overflow-menu">
                <button
                  type="button"
                  className="eh-overflow-trigger"
                  aria-label="More actions"
                  onClick={() => setIsMenuOpen((prev) => !prev)}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    more_vert
                  </span>
                </button>

                {isMenuOpen && (
                  <div className="eh-overflow-list eh-animate-in" role="menu">
                    {collapsedActions.map((action) => (
                      <div key={action.id} className="eh-overflow-item-wrap">
                        <button
                          type="button"
                          className="eh-overflow-item"
                          role="menuitem"
                          onClick={() => handlePrimaryAction(action, 'overflow')}
                        >
                          <span className="material-symbols-outlined" aria-hidden="true">
                            {action.icon}
                          </span>
                          <span>{action.displayLabel}</span>
                        </button>

                        {action.options &&
                          action.options.length > 0 &&
                          openOverflowDropdownId === action.id && (
                            <div className="eh-overflow-submenu eh-animate-in" role="menu">
                              {action.options.map((option) => (
                                <button
                                  key={option.id}
                                  type="button"
                                  className="eh-inline-dropdown-item"
                                  role="menuitem"
                                  onClick={() => {
                                    setSelectedLabelsByAction((previous) => ({
                                      ...previous,
                                      [action.id]: option.label,
                                    }));
                                    option.onClick?.();
                                    setOpenOverflowDropdownId(null);
                                    setIsMenuOpen(false);
                                  }}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="eh-fixed-actions" ref={fixedActionsRef}>
            {showFilterAction && (
              <div className={`eh-filter-wrap ${isFilterOpen ? 'eh-filter-wrap-open' : ''}`}>
                <button
                  type="button"
                  className="eh-filter-button"
                  aria-haspopup="dialog"
                  aria-expanded={isFilterOpen}
                  onClick={() => {
                    const next = !isFilterOpen;
                    onFilterOpenChange?.(next);
                    if (!isFilterOpen && next) onFilter?.();
                  }}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    filter_list
                  </span>
                  {filterLabel}
                </button>
              </div>
            )}
            <button type="button" className="eh-publish-button" onClick={onPublish}>
              {publishLabel}
            </button>
          </div>
        </div>
        )}
      </div>

      <div className="eh-measurer" aria-hidden="true">
        {inlineCandidates.map((action, index) => (
          <button
            key={`${action.id}-measure`}
            type="button"
            className="eh-collapsible-button"
            ref={(element) => {
              actionMeasureRefs.current[index] = element;
            }}
          >
            <span className="material-symbols-outlined">{action.icon}</span>
            <span>{action.displayLabel}</span>
          </button>
        ))}
        <button type="button" className="eh-overflow-trigger" ref={overflowMeasureRef}>
          <span className="material-symbols-outlined">more_vert</span>
        </button>
      </div>
    </header>
  );
}
